// Modèles de réponse aux candidatures (demandes de catégorie 'recrutement')
// et règle d'autorisation associée.
//
// SOURCE DE VÉRITÉ testable. La règle d'autorisation a une copie Deno dans
// supabase/functions/_shared/replyAuth.ts — garder les deux en sync.

export type ReplyTemplateKey = 'acknowledge' | 'meeting' | 'more_info' | 'reject';

export interface ReplyTemplate {
  key: ReplyTemplateKey;
  label: string;
  /** 'danger' = action lourde de conséquences, signalée visuellement (bouton rouge). */
  tone: 'normal' | 'danger';
}

export const REPLY_TEMPLATES: ReplyTemplate[] = [
  { key: 'acknowledge', label: 'Accusé de réception', tone: 'normal' },
  { key: 'meeting', label: "Convenir d'un rendez-vous", tone: 'normal' },
  { key: 'more_info', label: "Besoin d'informations", tone: 'normal' },
  { key: 'reject', label: 'Candidature refusée', tone: 'danger' },
];

/** « Marine Quiviger » → « Marine ». Chaîne vide si absent. */
export function extractFirstName(fullName: string | null | undefined): string {
  if (!fullName) return '';
  const trimmed = fullName.trim();
  if (!trimmed) return '';
  return trimmed.split(/\s+/)[0] ?? '';
}

const SIGNATURE = (lawyerName: string) => `Cordialement,\n${lawyerName}\nCabinet CIME Avocats`;

/** « Bonjour Marine, » ou « Bonjour, » si le prénom est inconnu (jamais « Bonjour , »). */
function greeting(firstName: string): string {
  return firstName ? `Bonjour ${firstName},` : 'Bonjour,';
}

export function renderReplyTemplate(
  key: ReplyTemplateKey,
  senderName: string | null,
  lawyerName: string,
): { subject: string; body: string } {
  const hello = greeting(extractFirstName(senderName));
  const sign = SIGNATURE(lawyerName);

  switch (key) {
    case 'acknowledge':
      return {
        subject: 'Votre candidature — Cabinet CIME Avocats',
        body: [
          hello,
          '',
          "Nous avons bien reçu votre candidature et vous remercions de l'intérêt que vous portez au cabinet.",
          '',
          'Votre dossier est en cours d\'examen. Nous reviendrons vers vous prochainement.',
          '',
          sign,
        ].join('\n'),
      };

    case 'meeting':
      return {
        subject: 'Votre candidature — proposition d\'entretien',
        body: [
          hello,
          '',
          'Votre candidature a retenu notre attention et nous souhaiterions vous rencontrer.',
          '',
          'Pourriez-vous nous indiquer vos disponibilités pour un entretien dans les prochaines semaines ? Vous pouvez répondre directement à ce message.',
          '',
          sign,
        ].join('\n'),
      };

    case 'more_info':
      return {
        subject: 'Votre candidature — informations complémentaires',
        body: [
          hello,
          '',
          "Nous avons bien reçu votre candidature. Afin de poursuivre son examen, nous aurions besoin d'éléments complémentaires.",
          '',
          "[à préciser par l'avocat]",
          '',
          'Vous pouvez répondre directement à ce message.',
          '',
          sign,
        ].join('\n'),
      };

    case 'reject':
      return {
        subject: 'Votre candidature — Cabinet CIME Avocats',
        body: [
          hello,
          '',
          'Nous avons étudié votre candidature avec attention.',
          '',
          "Nous ne sommes malheureusement pas en mesure d'y donner une suite favorable. Cette décision ne remet nullement en cause vos qualités, et nous vous souhaitons pleine réussite dans vos recherches.",
          '',
          "Nous vous remercions de l'intérêt porté au cabinet.",
          '',
          sign,
        ].join('\n'),
      };
  }
}

export type ReplyAuthCollab = { id: string; role: string };
export type ReplyAuthTicket = {
  assigned_to: string | null;
  referent_id: string | null;
  sender_email: string | null;
  category_slug: string | null;
};

/**
 * Qui peut écrire au candidat : admin OU l'assigné OU le référent de CETTE demande,
 * et uniquement sur une demande de recrutement ayant un email d'expéditeur.
 * Appliquée côté UI (masquer) ET côté serveur (interdire).
 */
export function canReplyToCandidate(
  collab: ReplyAuthCollab | null,
  ticket: ReplyAuthTicket | null,
): boolean {
  if (!collab || !ticket) return false;
  if (ticket.category_slug !== 'recrutement') return false;
  if (!ticket.sender_email) return false;

  if (collab.role === 'admin') return true;
  return ticket.assigned_to === collab.id || ticket.referent_id === collab.id;
}
